/**
 * Cushion Table Component - Display detailed cushion production data in table format
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Database } from 'lucide-react';
import { DailyCushionData } from '@/types/cushion';
import { formatNumberDisplay } from '@/lib/utils';

interface CushionTableProps {
  dailyData: DailyCushionData[];
  isLoading?: boolean;
  error?: string | null;
  title?: string;
  processes?: string[];
}

export const CushionTable: React.FC<CushionTableProps> = ({ 
  dailyData, 
  isLoading = false, 
  error = null,
  title = "Dữ liệu sản xuất nệm mút chi tiết",
  processes = ['catVai', 'may', 'thanhPham', 'doMut', 'catMut', 'gon']
}) => {
  // Process configurations
  const processConfig = {
    catVai: { name: 'Cắt vải', color: 'bg-blue-50' },
    may: { name: 'May', color: 'bg-green-50' },
    thanhPham: { name: 'Thành phẩm', color: 'bg-amber-50' },
    doMut: { name: 'Đổ mút', color: 'bg-teal-50' },
    catMut: { name: 'Cắt mút', color: 'bg-red-50' },
    gon: { name: 'Gòn', color: 'bg-gray-50' }
  };
  // Sort data by date (newest first)
  const sortedData = useMemo(() => {
    return [...dailyData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [dailyData]);

  // Format number with decimal places
  // Use Vietnamese locale formatting for numbers

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <Skeleton className="h-6 w-64" />
          </CardTitle>
          <CardDescription>
            <Skeleton className="h-4 w-48" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-red-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-600">
            <p>Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (sortedData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Không có dữ liệu sản xuất nệm mút trong khoảng thời gian được chọn</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-green-600" />
          📋 {title}
        </CardTitle>
        <CardDescription>
          Chi tiết dữ liệu sản xuất theo từng công đoạn và ngày ({sortedData.length} ngày)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="min-w-full">
            <TableHeader>
              {/* First row: Process names (merged cells) */}
              <TableRow>
                <TableHead rowSpan={2} className="vertical-align-bottom border-r w-[120px]">Ngày</TableHead>
                
                {/* Dynamic process headers with merged cells */}
                {processes.map((process, index) => {
                  const config = processConfig[process as keyof typeof processConfig];
                  const isLast = index === processes.length - 1;
                  return (
                    <TableHead 
                      key={process}
                      colSpan={3} 
                      className={`text-center ${config.color} ${isLast ? '' : 'border-r'}`}
                    >
                      {config.name}
                    </TableHead>
                  );
                })}
              </TableRow>
              
              {/* Second row: Column labels */}
              <TableRow>
                {/* Dynamic column labels for each process */}
                {processes.map((process, index) => {
                  const isLast = index === processes.length - 1;
                  return (
                    <React.Fragment key={`cols-${process}`}>
                      <TableHead className="text-xs text-center border-r">Công</TableHead>
                      <TableHead className="text-xs text-center border-r">
                        {process === 'gon' ? 'Kg' : 'M³'}
                      </TableHead>
                      <TableHead className={`text-xs text-center ${isLast ? '' : 'border-r'}`}>NS</TableHead>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((item, index) => (
                <TableRow key={index} className="hover:bg-gray-50">
                  {/* Ngày */}
                  <TableCell className="font-medium text-center border-r">
                    {new Date(item.date).toLocaleDateString('vi-VN')}
                  </TableCell>
                  
                  {/* Dynamic data cells based on selected processes */}
                  {processes.map((process, index) => {
                    const processData = item.processes[process as keyof typeof item.processes];
                    const config = processConfig[process as keyof typeof processConfig];
                    const isLast = index === processes.length - 1;
                    
                    return (
                      <React.Fragment key={`data-${process}`}>
                        {/* Labor */}
                        <TableCell className="text-sm text-center border-r">
                          {formatNumberDisplay(processData.labor || 0, 0)}
                        </TableCell>
                        
                        {/* Volume/Weight */}
                        <TableCell className={
                          process === 'gon' 
                            ? "text-sm text-center bg-gray-50/30 border-r" 
                            : `text-sm text-center ${config.color}/30 border-r`
                        }>
                                                    {process === 'gon' 
                            ? formatNumberDisplay(processData.weight || 0, 2)
                            : formatNumberDisplay(processData.volume || 0, 2)
                          }
                        </TableCell>
                        
                        {/* Productivity */}
                        <TableCell className={`text-sm text-center ${isLast ? '' : 'border-r'}`}>
                          {formatNumberDisplay(processData.productivity || 0, 3)}
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};